import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Check, Truck, Shield, ShoppingBag, Star } from 'lucide-react';

const renderStars = (rating, size = 'h-4 w-4') => {
  const n = Number(rating || 0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((v) => (
        <Star
          key={v}
          className={`${size} ${v <= n ? 'text-amber-500 fill-amber-500' : 'text-amber-200'}`}
        />
      ))}
    </div>
  );
};

const formatMetricLabel = (metric) => {
  if (!metric) return "Flavor";
  return metric.charAt(0).toUpperCase() + metric.slice(1);
};

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ count: 0, avg_rating: 0 });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  const fetchReviews = async (productId) => {
    try {
      setReviewsLoading(true);
      const response = await api.get(`/reviews/product/${productId}`);
      setReviews(response.data?.reviews || []);
      setReviewSummary(response.data?.summary || { count: 0, avg_rating: 0 });
    } catch {
      setReviews([]);
      setReviewSummary({ count: 0, avg_rating: 0 });
    } finally {
      setReviewsLoading(false);
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${slug}`);
      const fetchedProduct = response.data;
      setProduct(fetchedProduct);
      if (fetchedProduct.variants?.length > 0) {
        setSelectedVariant(fetchedProduct.variants[0]);
      }
      if (fetchedProduct.id) {
        fetchReviews(fetchedProduct.id);
      }
    } catch {
      toast.error('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) return;
    try {
      await addToCart(selectedVariant.id, product.id, 1);
    } catch {
      toast.error('Failed to add item to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <p className="text-amber-800">Loading product...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="text-center">
          <p className="mb-4 text-amber-900">Product not found</p>
          <Link to="/shop">
            <Button className="bg-amber-700 hover:bg-amber-800 text-white">Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="text-sm text-amber-700 mb-6">
          <Link to="/" className="hover:text-amber-900">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/shop" className="hover:text-amber-900">Shop</Link>
          <span className="mx-2">/</span>
          <span className="text-amber-900 font-medium">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden shadow-xl">
            <div className="relative">
              <span className="absolute top-4 left-4 z-10 bg-white/90 border border-amber-200 text-amber-900 text-xs font-semibold px-3 py-1 rounded-full">
                Premium Quality
              </span>
              <img
                src={product.image_urls?.[0] || '/placeholder-product.png'}
                alt={product.name}
                className="w-full h-[520px] md:h-[560px] object-cover"
              />
            </div>
            <div className="border-t border-amber-200 bg-amber-50/60 p-4">
              <h3 className="text-sm font-semibold text-amber-950 mb-3">Product Assurance</h3>
              <div className="grid sm:grid-cols-3 gap-3 text-xs">
                <div className="flex items-start gap-2 text-amber-800 bg-white border border-amber-200 rounded-lg p-2.5">
                  <Truck className="h-4 w-4 text-amber-600 mt-0.5" />
                  <span>Free shipping above {'\u20B9'}599</span>
                </div>
                <div className="flex items-start gap-2 text-amber-800 bg-white border border-amber-200 rounded-lg p-2.5">
                  <Shield className="h-4 w-4 text-amber-600 mt-0.5" />
                  <span>100% authentic and pure</span>
                </div>
                <div className="flex items-start gap-2 text-amber-800 bg-white border border-amber-200 rounded-lg p-2.5">
                  <Check className="h-4 w-4 text-amber-600 mt-0.5" />
                  <span>Freshness-safe packaging</span>
                </div>
              </div>
            </div>
            <div className="border-t border-amber-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-amber-950 mb-3">Spice Intensity Profile</h3>
              <div className="grid sm:grid-cols-2 gap-3 text-xs">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 uppercase tracking-wide">{formatMetricLabel(product.intensity_metric)} Intensity</p>
                  <p className="text-amber-950 font-bold text-sm mt-1">
                    {(product.intensity_level || "medium").toUpperCase()}
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-700 uppercase tracking-wide">Category</p>
                  <p className="text-amber-950 font-bold text-sm mt-1 capitalize">{product.category}</p>
                  {product.intensity_notes ? (
                    <p className="text-amber-700 mt-2 leading-relaxed">{product.intensity_notes}</p>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="border-t border-amber-200 bg-amber-50/50 p-4">
              <h3 className="text-sm font-semibold text-amber-950 mb-2">Customer Snapshot</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {renderStars(Math.round(reviewSummary.avg_rating || 0), 'h-4 w-4')}
                  <span className="font-semibold text-amber-900">{Number(reviewSummary.avg_rating || 0).toFixed(1)}</span>
                </div>
                <span className="text-xs text-amber-700">{reviewSummary.count || 0} verified review(s)</span>
              </div>
              {reviews[0]?.comment ? (
                <p className="text-xs text-amber-800 mt-2 line-clamp-3">
                  “{reviews[0].comment}”
                </p>
              ) : (
                <p className="text-xs text-amber-800 mt-2">
                  Customer feedback appears here after delivered-order reviews are submitted.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm">
              <h1 className="text-4xl md:text-5xl font-bold text-amber-950 leading-tight">{product.name}</h1>
              <p className="text-amber-800 mt-3 leading-relaxed">{product.description}</p>

              <div className="flex items-center justify-between mt-5">
                <div className="flex items-center gap-2">
                  {renderStars(Math.round(reviewSummary.avg_rating || 0), 'h-4 w-4')}
                  <span className="text-sm font-semibold text-amber-900">
                    {Number(reviewSummary.avg_rating || 0).toFixed(1)}
                  </span>
                  <span className="text-sm text-amber-700">({reviewSummary.count || 0} reviews)</span>
                </div>
                <span className="text-xs text-amber-700">GST-ready billing available</span>
              </div>

              {selectedVariant && (
                <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-100">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-3xl font-bold text-amber-950">Rs {Number(selectedVariant.selling_price || 0).toFixed(2)}</span>
                    {Number(selectedVariant.discount_percent || 0) > 0 && (
                      <>
                        <span className="text-lg line-through text-amber-600">Rs {Number(selectedVariant.mrp || 0).toFixed(2)}</span>
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                          {selectedVariant.discount_percent}% OFF
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-amber-700 mt-1">Inclusive of applicable taxes</p>
                </div>
              )}

              <div className="mt-6">
                <h3 className="font-semibold text-amber-950 mb-3">Select Variant</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {product.variants.map((variant) => {
                    const active = selectedVariant?.id === variant.id;
                    return (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        className={`px-4 py-3 border rounded-xl text-left transition ${
                          active
                            ? 'border-amber-600 bg-amber-100 text-amber-950 shadow-sm'
                            : 'border-amber-200 bg-white text-amber-800 hover:border-amber-400'
                        }`}
                      >
                        <p className="font-semibold">{variant.size}</p>
                        <p className="text-xs mt-0.5">Rs {Number(variant.selling_price || 0).toFixed(2)}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedVariant && (
                <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border border-amber-200 bg-white">
                  {selectedVariant.in_stock ? (
                    <span className="text-green-700">In Stock</span>
                  ) : (
                    <span className="text-red-700">Out of Stock</span>
                  )}
                </div>
              )}

              <Button
                onClick={handleAddToCart}
                disabled={!selectedVariant?.in_stock || !selectedVariant?.is_active}
                className="w-full mt-5 bg-amber-700 hover:bg-amber-800 text-white py-6 rounded-xl text-base font-semibold flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-5 h-5" />
                Add to Cart
              </Button>

              <div className="grid sm:grid-cols-2 gap-3 pt-6 mt-6 border-t border-amber-200">
                <div className="flex items-start gap-2 text-amber-800 text-sm">
                  <Truck className="h-4 w-4 text-amber-600 mt-0.5" />
                  <span>Free shipping on orders above {'\u20B9'}599</span>
                </div>
                <div className="flex items-start gap-2 text-amber-800 text-sm">
                  <Shield className="h-4 w-4 text-amber-600 mt-0.5" />
                  <span>100% Authentic & Pure</span>
                </div>
              </div>
            </div>

            {product.benefits?.length > 0 && (
              <div className="bg-white border border-amber-200 rounded-2xl p-6 shadow-sm">
                <h3 className="font-bold text-amber-950 mb-3">Health Benefits</h3>
                <ul className="space-y-2">
                  {product.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                      <Check className="h-4 w-4 text-amber-600 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">
                <strong>No Return Policy:</strong> Due to food safety reasons, returns and exchanges are not accepted.
              </p>
            </div>
          </div>
        </div>

        <section className="mt-10 bg-white rounded-2xl border border-amber-200 p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="text-2xl font-bold text-amber-950">Customer Reviews</h2>
            <div className="text-right">
              <div className="flex items-center justify-end gap-2">
                {renderStars(Math.round(reviewSummary.avg_rating || 0), 'h-4 w-4')}
                <span className="font-semibold text-amber-900">{Number(reviewSummary.avg_rating || 0).toFixed(1)}</span>
              </div>
              <p className="text-xs text-amber-700">{reviewSummary.count || 0} review(s)</p>
            </div>
          </div>

          {reviewsLoading ? (
            <p className="text-amber-700">Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <p className="text-amber-700">No reviews yet. Be the first to review after delivery.</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <article key={review.id} className="border border-amber-200 rounded-xl p-4 bg-amber-50/40">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-amber-950">{review.user_name || 'Customer'}</p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        {review.created_at ? new Date(review.created_at).toLocaleDateString('en-IN') : ''}
                      </p>
                    </div>
                    {renderStars(review.rating, 'h-4 w-4')}
                  </div>

                  {review.comment ? <p className="text-amber-800 mt-2 text-sm">{review.comment}</p> : null}

                  {Array.isArray(review.image_urls) && review.image_urls.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {review.image_urls.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer">
                          <img
                            src={url}
                            alt="review"
                            className="w-full h-16 object-cover rounded-lg border border-amber-200"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProductDetail;
